import { useEffect, useState } from 'react';
import { useAuthContext } from '../../../hooks/AuthContext';
import { useControllerFlowStepsContext } from '../../../hooks/ControllerFlowStepsContext';
import Icon from '../../../components/Icon/Icon';
import Button from '../../../components/Button/Button';
import ButtonLabel from '../../../components/Button/ButtonLabel';
import FormFieldTokenNumber from '../../../components/FormField/FormFieldTokenNumber';
import Loading from '../../../components/Loading/Loading';
import { useSearchParams } from 'react-router-dom';
import {
  updateUserInfos,
  updateUserEmail,
  updateTelephone,
  updateAddress,
  validateUserIdentity
} from '../../../services/user';
import { checkEmailExist } from '../../../services/queryController';
import {
  createTokenValidateNewEmail,
  validateCodeFromEmail,
  createTokenValidatePhone,
  validateCodeFromCellphone
} from '../../../services/tokenValidation';
import { getCep } from '../../../services/cities';
import { SendCodeToEmailEventsEnum } from '../../../enums/SendCodeToEmailEventsEnum';
import { refreshToken } from '../../../services/api';
import { getRefreshTokenSession } from '../../../utils/auth';
import { mobilePhoneMask } from '../../../utils/Masks';
import { formatPhone } from '../../../utils/GenericFunctions';
import { ErrorMessages } from '../../../data/ErrorMessages';

interface DataRow {
  icon: string;
  label: string;
  value: string;
  field?: string;
  editType?:
    | 'email'
    | 'phone'
    | 'address'
    | 'firstName'
    | 'lastName'
    | 'birthDate';
}

type EditMode =
  | null
  | 'email-1'
  | 'email-2'
  | 'email-3'
  | 'phone-1'
  | 'phone-2'
  | 'address'
  | 'birthDate';

const SUBLINKS = [
  { title: 'Visão geral', id: 'visao-geral' },
  { title: 'Detalhes sobre mim', id: 'detalhes-sobre-mim' },
  { title: 'Informações de contato', id: 'informacoes-de-contato' },
  // { title: 'Trabalho e educação', id: 'trabalho-e-educacao' },
  { title: 'Lugares onde morei', id: 'lugares-onde-morei' },
  { title: 'Baixar dados', id: 'baixar-dados' }
  // { title: 'Família', id: 'familia' }
];

function formatBirthDate(bd: any): string {
  if (!bd) return '—';
  let d: Date;
  if (Array.isArray(bd)) {
    d = new Date(bd[0], bd[1] - 1, bd[2]);
  } else {
    const s = String(bd);
    d = s.includes('/')
      ? new Date(
          Number(s.split('/')[2]),
          Number(s.split('/')[1]) - 1,
          Number(s.split('/')[0])
        )
      : new Date(s);
    if (isNaN(d.getTime())) return s;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(d);
}

function getRows(id: string, user: any): DataRow[] {
  const name = user?.userName || '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '—';
  const lastName = parts.slice(1).join(' ') || '—';
  const socialName = (user?.hasSocialName && user?.socialName) || '—';
  const email = user?.email || '—';
  const phone = user?.cellPhoneNumber ? formatPhone(user.cellPhoneNumber) : '—';
  const birthDate = formatBirthDate(user?.birthDate);
  const gender = user?.gender || '—';
  const kindGender = user?.kindGender || '—';

  switch (id) {
    case 'visao-geral':
      return [
        {
          icon: 'person',
          label: 'Meu nome',
          value: firstName,
          editType: 'firstName'
        },
        {
          icon: 'person',
          label: 'Meu sobrenome',
          value: lastName,
          editType: 'lastName'
        },
        {
          icon: 'person',
          label: 'Meu nome social',
          value: socialName,
          field: 'socialName'
        },
        {
          icon: 'birthdate',
          label: 'Minha data de nascimento',
          value: birthDate
        },
        {
          icon: 'person',
          label: 'Meus pronomes',
          value: gender,
          field: 'gender'
        },
        {
          icon: 'personId',
          label: 'Como me identifico',
          value: kindGender,
          field: 'kindGender'
        }
      ];
    case 'detalhes-sobre-mim':
      return [
        {
          icon: 'person',
          label: 'Nome completo',
          value: name,
          field: 'userName'
        },
        {
          icon: 'birthdate',
          label: 'Data de nascimento',
          value: birthDate,
          editType: 'birthDate'
        },
        { icon: 'person', label: 'Gênero', value: gender, field: 'gender' }
      ];
    case 'informacoes-de-contato':
      return [
        { icon: 'email', label: 'E-mail', value: email, editType: 'email' },
        { icon: 'mobile', label: 'Celular', value: phone, editType: 'phone' }
      ];
    case 'trabalho-e-educacao':
      return [
        { icon: 'person', label: 'Profissão', value: '—' },
        { icon: 'person', label: 'Empresa', value: '—' },
        { icon: 'person', label: 'Formação', value: '—' }
      ];
    case 'lugares-onde-morei':
      return [
        {
          icon: 'address',
          label: 'CEP',
          value: user?.cep || '—',
          editType: 'address'
        },
        { icon: 'address', label: 'Cidade', value: user?.city || '—' },
        { icon: 'address', label: 'Estado', value: user?.state || '—' },
        { icon: 'address', label: 'Bairro', value: user?.neighborhood || '—' },
        {
          icon: 'address',
          label: 'Rua / Avenida',
          value: user?.streetName || '—'
        }
      ];
    case 'familia':
      return [{ icon: 'person', label: 'Estado civil', value: '—' }];
    default:
      return [];
  }
}

export default function SobreTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, setUser, checkAuth } = useAuthContext();
  const { setAlertBox, showErrorMessageServer } =
    useControllerFlowStepsContext();
  const [selected, setSelected] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // simple inline edit
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // name edit
  const [nameField, setNameField] = useState<'firstName' | 'lastName' | null>(
    null
  );
  const [nameValue, setNameValue] = useState('');

  // contact / address panel
  const [editMode, setEditMode] = useState<EditMode>(null);

  // email wizard
  const [emailPassword, setEmailPassword] = useState('');
  const [identityCode, setIdentityCode] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailToken, setEmailToken] = useState({ value: '', isValid: false });
  const [emailTokenStatus, setEmailTokenStatus] = useState<
    'none' | 'error' | 'success'
  >('none');
  const [emailTokenValidated, setEmailTokenValidated] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // phone wizard
  const [newPhone, setNewPhone] = useState('');
  const [phoneToken, setPhoneToken] = useState({ value: '', isValid: false });
  const [phoneTokenStatus, setPhoneTokenStatus] = useState<
    'none' | 'error' | 'success'
  >('none');
  const [phoneLoading, setPhoneLoading] = useState(false);

  // address form
  const [addr, setAddr] = useState({
    cep: '',
    city: '',
    state: '',
    neighborhood: '',
    streetName: '',
    addressNumber: '',
    complement: ''
  });
  const [addrNoNumber, setAddrNoNumber] = useState(false);
  const [addrCityDisabled, setAddrCityDisabled] = useState(true);
  const [addrLoading, setAddrLoading] = useState(false);

  // email password visibility
  const [showEmailPass, setShowEmailPass] = useState(false);

  // birthDate edit
  const [newBirthDate, setNewBirthDate] = useState('');

  const userName =
    (user?.hasSocialName && user?.socialName) || user?.userName || 'Usuário';
  const currentSubLink = SUBLINKS[selected];
  const rows = getRows(currentSubLink.id, user);

  useEffect(() => {
    const currentSection = searchParams.get('secao');
    const sectionIndex = SUBLINKS.findIndex(
      (link) => link.id === currentSection
    );
    setSelected(sectionIndex >= 0 ? sectionIndex : 0);
  }, [searchParams]);

  function getNameParts() {
    const full = (user?.userName || '').trim().split(/\s+/).filter(Boolean);
    return { firstName: full[0] || '', lastName: full.slice(1).join(' ') };
  }

  // ── simple field edit ──────────────────────────────────────────────────────
  async function saveSimpleEdit(field: string) {
    setSaving(true);
    try {
      await updateUserInfos({ [field]: editValue });
      if (user) setUser({ ...user, [field]: editValue } as any);
      setAlertBox({ message: 'Informação atualizada', error: false });
      setEditingField(null);
    } catch {
      showErrorMessageServer();
    } finally {
      setSaving(false);
    }
  }

  // ── name edit ──────────────────────────────────────────────────────────────
  async function saveNameEdit() {
    if (!nameField) return;
    setSaving(true);
    const { firstName, lastName } = getNameParts();
    const full = (
      nameField === 'firstName'
        ? `${nameValue.trim()} ${lastName}`
        : `${firstName} ${nameValue.trim()}`
    ).trim();
    try {
      await updateUserInfos({ userName: full });
      if (user) setUser({ ...user, userName: full } as any);
      setAlertBox({ message: 'Nome atualizado', error: false });
      setNameField(null);
    } catch {
      showErrorMessageServer();
    } finally {
      setSaving(false);
    }
  }

  // ── email wizard ───────────────────────────────────────────────────────────
  function openEmailEdit() {
    setShowEmailPass(false);
    setEmailPassword('');
    setIdentityCode('');
    setNewEmail('');
    setConfirmEmail('');
    setEmailToken({ value: '', isValid: false });
    setEmailTokenStatus('none');
    setEmailTokenValidated(false);
    setEmailError('');
    setEditMode('email-1');
  }

  async function emailAuthSubmit() {
    setEmailLoading(true);
    setEmailError('');
    try {
      const res = await validateUserIdentity({
        email: checkAuth().email,
        password: emailPassword
      });
      if (res?.indentityValidated) {
        setIdentityCode(res?.code || '');
        setEditMode('email-2');
      } else setEmailError('Senha incorreta');
    } catch {
      showErrorMessageServer();
    } finally {
      setEmailLoading(false);
    }
  }

  async function emailNewEmailSubmit() {
    if (newEmail !== confirmEmail) {
      setEmailError('Os e-mails não conferem');
      return;
    }
    setEmailLoading(true);
    setEmailError('');
    try {
      const res: any = await checkEmailExist(newEmail);
      if (res?.exist) {
        setEmailError('E-mail já vinculado a outra conta');
        setEmailLoading(false);
        return;
      }
      await createTokenValidateNewEmail(newEmail);
      setEmailToken({ value: '', isValid: false });
      setEmailTokenStatus('none');
      setEmailTokenValidated(false);
      setEditMode('email-3');
    } catch {
      showErrorMessageServer();
    } finally {
      setEmailLoading(false);
    }
  }

  async function validateEmailToken() {
    if (!emailToken.isValid || emailTokenValidated) return;
    setEmailLoading(true);
    try {
      await validateCodeFromEmail(
        newEmail,
        emailToken.value,
        SendCodeToEmailEventsEnum.CONFIRM_NEW_EMAIL,
        ErrorMessages.CODE_EMAIL_NEW_EMAIL
      );
      setEmailTokenStatus('success');
      setEmailTokenValidated(true);
    } catch {
      setEmailTokenStatus('error');
    } finally {
      setEmailLoading(false);
    }
  }

  async function saveEmail() {
    if (!emailTokenValidated) return;
    setEmailLoading(true);
    try {
      await updateUserEmail({
        newEmail,
        identityVerifierCode: identityCode,
        newEmailCode: emailToken.value
      });
      await refreshToken(getRefreshTokenSession() || '');
      if (user) setUser({ ...user, email: newEmail } as any);
      setAlertBox({ message: 'E-mail alterado com sucesso', error: false });
      setEditMode(null);
    } catch {
      showErrorMessageServer();
    } finally {
      setEmailLoading(false);
    }
  }

  useEffect(() => {
    if (emailToken.isValid && editMode === 'email-3') validateEmailToken();
  }, [emailToken.isValid]);

  // ── phone wizard ───────────────────────────────────────────────────────────
  function openPhoneEdit() {
    setNewPhone('');
    setPhoneToken({ value: '', isValid: false });
    setPhoneTokenStatus('none');
    setEditMode('phone-1');
  }

  async function phoneSendSms() {
    setPhoneLoading(true);
    try {
      await createTokenValidatePhone(newPhone.replace(/\D/g, ''));
      setPhoneToken({ value: '', isValid: false });
      setPhoneTokenStatus('none');
      setEditMode('phone-2');
    } catch {
      showErrorMessageServer();
    } finally {
      setPhoneLoading(false);
    }
  }

  async function phoneTokenSubmit() {
    const clean = newPhone.replace(/\D/g, '');
    setPhoneLoading(true);
    try {
      await validateCodeFromCellphone(
        clean,
        phoneToken.value,
        ErrorMessages.CODE_PHONE_ADD_PHONE
      );
      await updateTelephone(clean, phoneToken.value);
      if (user) setUser({ ...user, cellPhoneNumber: clean } as any);
      setAlertBox({ message: 'Celular atualizado com sucesso', error: false });
      setEditMode(null);
    } catch {
      setPhoneTokenStatus('error');
    } finally {
      setPhoneLoading(false);
    }
  }

  useEffect(() => {
    if (phoneToken.isValid && editMode === 'phone-2') phoneTokenSubmit();
  }, [phoneToken.isValid]);

  // ── address form ───────────────────────────────────────────────────────────
  function openAddressEdit() {
    setAddr({
      cep: user?.cep || '',
      city: user?.city || '',
      state: user?.state || '',
      neighborhood: user?.neighborhood || '',
      streetName: user?.streetName || '',
      addressNumber: user?.addressNumber || '',
      complement: user?.complement || ''
    });
    setAddrNoNumber(user?.cep ? !user?.hasAddressNumber : false);
    setAddrCityDisabled(true);
    setEditMode('address');
  }

  async function handleCepBlur(cepVal: string) {
    const cleaned = cepVal.replace(/\D/g, '');
    if (cleaned.length !== 8) return;
    try {
      const data: any = await getCep(cleaned);
      if (data?.uf) {
        setAddrCityDisabled(true);
        setAddr((a) => ({
          ...a,
          state: data.uf,
          city: data.localidade || a.city,
          streetName: data.logradouro || a.streetName,
          neighborhood: data.bairro || a.neighborhood
        }));
      } else {
        setAddrCityDisabled(false);
      }
    } catch {
      setAddrCityDisabled(false);
    }
  }

  async function saveAddress() {
    setAddrLoading(true);
    try {
      await updateAddress({
        cep: addr.cep.replace(/\D/g, ''),
        city: addr.city,
        state: addr.state,
        neighborhood: addr.neighborhood,
        streetName: addr.streetName,
        addressNumber: addrNoNumber ? '' : addr.addressNumber,
        hasAddressNumber: !addrNoNumber,
        complement: addr.complement
      });
      if (user)
        setUser({ ...user, ...addr, hasAddressNumber: !addrNoNumber } as any);
      setAlertBox({ message: 'Endereço atualizado', error: false });
      setEditMode(null);
    } catch {
      showErrorMessageServer();
    } finally {
      setAddrLoading(false);
    }
  }

  // ── birthDate ──────────────────────────────────────────────────────────────
  function getBirthDateInputValue(): string {
    const bd = user?.birthDate;
    if (!bd) return '';
    if (Array.isArray(bd))
      return `${bd[0]}-${String(bd[1]).padStart(2, '0')}-${String(bd[2]).padStart(2, '0')}`;
    const s = String(bd);
    if (s.includes('/')) {
      const [d, m, y] = s.split('/');
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return s;
  }

  function openBirthDateEdit() {
    setNewBirthDate(getBirthDateInputValue());
    setEditMode('birthDate');
  }

  async function saveBirthDate() {
    if (!newBirthDate) return;
    setSaving(true);
    try {
      await updateUserInfos({ birthDate: newBirthDate });
      if (user) setUser({ ...user, birthDate: newBirthDate } as any);
      setAlertBox({ message: 'Data de nascimento atualizada', error: false });
      setEditMode(null);
    } catch {
      showErrorMessageServer();
    } finally {
      setSaving(false);
    }
  }

  // ── helpers ────────────────────────────────────────────────────────────────
  function closeAll() {
    setEditMode(null);
    setEditingField(null);
    setNameField(null);
  }

  function handleNavSelect(index: number) {
    setSelected(index);
    closeAll();
    setIsMenuOpen(false);

    const params = new URLSearchParams(searchParams);
    const selectedSection = SUBLINKS[index]?.id;

    if (!selectedSection || selectedSection === 'visao-geral') {
      params.delete('secao');
    } else {
      params.set('secao', selectedSection);
    }

    setSearchParams(params, { replace: true });
  }

  function toggleMenu() {
    setIsMenuOpen((prev) => !prev);
  }

  return (
    <div className="user-profile__content">
      <div className="user-profile__left-rail">
        <div
          className={`user-profile__left-rail-header${isMenuOpen ? ' user-profile__left-rail-header--open' : ''}`}
          onClick={toggleMenu}
        >
          <span>Sobre</span>
        </div>
        <div className="user-profile__left-rail-divider" />
        <div
          className={`user-profile__left-rail-nav${isMenuOpen ? ' user-profile__left-rail-nav--open' : ''}`}
        >
          <div className="user-profile__nav-user">
            <div className="user-profile__nav-avatar">
              <div className="user-profile__nav-avatar-initial">
                {userName.charAt(0).toUpperCase()}
              </div>
              <span className="user-profile__nav-avatar-badge" />
            </div>
            <span className="user-profile__nav-username">{userName}</span>
          </div>
          {SUBLINKS.map((link, index) => (
            <button
              key={link.id}
              className={`user-profile__nav-item${selected === index ? ' user-profile__nav-item--active' : ''}`}
              onClick={() => handleNavSelect(index)}
            >
              {link.title}
            </button>
          ))}
        </div>
      </div>

      <div className="user-profile__main">
        <div className="user-profile__main-header">
          <span className="user-profile__main-title">
            {currentSubLink.title}
          </span>
        </div>
        <div className="user-profile__main-divider" />

        {editMode ? (
          <div className="user-profile__edit-panel">
            {editMode === 'email-1' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">alterar e-mail</h2>
                <span className="md mt-8">
                  Para alterar seu e-mail, confirme sua senha primeiro.
                </span>
                <div className="flex flex-column mt-16">
                  <div className="form-container">
                    <label className="form-field-label">senha atual</label>
                    <div className="form-field">
                      <input
                        className="caption"
                        type={showEmailPass ? 'text' : 'password'}
                        placeholder="Digite sua senha"
                        value={emailPassword}
                        onChange={(e) => setEmailPassword(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && emailAuthSubmit()
                        }
                      />
                      <div
                        className="form-field-sufix"
                        onClick={() => setShowEmailPass((p) => !p)}
                      >
                        <Icon name={showEmailPass ? 'eyeClose' : 'eye'} />
                      </div>
                    </div>
                  </div>
                  {emailError && (
                    <p className="ctv-error-message mt-8">{emailError}</p>
                  )}
                  <Button
                    className="mt-16 mbi-0"
                    full
                    loading={emailLoading}
                    disabled={!emailPassword || emailLoading}
                    onClick={emailAuthSubmit}
                  >
                    <ButtonLabel loading={emailLoading}>continuar</ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={closeAll}
                  >
                    <ButtonLabel>cancelar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'email-2' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">novo e-mail</h2>
                <span className="md mt-8">
                  Digite e confirme seu novo e-mail.
                </span>
                <div className="flex flex-column mt-16">
                  <div className="form-container">
                    <label className="form-field-label">novo e-mail</label>
                    <div className="form-field">
                      <input
                        className="caption"
                        type="email"
                        placeholder="Digite o novo e-mail"
                        value={newEmail}
                        onChange={(e) => {
                          setNewEmail(e.target.value);
                          setEmailError('');
                        }}
                      />
                    </div>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">
                      confirme o e-mail
                    </label>
                    <div className="form-field">
                      <input
                        className="caption"
                        type="email"
                        placeholder="Confirme o novo e-mail"
                        value={confirmEmail}
                        onChange={(e) => {
                          setConfirmEmail(e.target.value);
                          setEmailError('');
                        }}
                      />
                    </div>
                  </div>
                  {emailError && (
                    <p className="ctv-error-message mt-8">{emailError}</p>
                  )}
                  <Button
                    className="mt-16 mbi-0"
                    full
                    loading={emailLoading}
                    disabled={!newEmail || !confirmEmail || emailLoading}
                    onClick={emailNewEmailSubmit}
                  >
                    <ButtonLabel loading={emailLoading}>
                      enviar código
                    </ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={() => setEditMode('email-1')}
                  >
                    <ButtonLabel>voltar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'email-3' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">confirmar e-mail</h2>
                <span className="md mt-8">
                  Código enviado para <strong>{newEmail}</strong>
                </span>
                <div className="mt-16">
                  <FormFieldTokenNumber
                    fieldValidate={emailToken}
                    status={emailTokenStatus}
                    setState={setEmailToken}
                    resendingCode={false}
                  />
                </div>
                {emailLoading && (
                  <div className="user-profile__edit-panel-loader">
                    <Loading />
                  </div>
                )}
                <div className="flex flex-column mt-16">
                  <Button
                    className="mbi-0"
                    full
                    loading={emailLoading}
                    disabled={!emailTokenValidated || emailLoading}
                    onClick={saveEmail}
                  >
                    <ButtonLabel loading={emailLoading}>
                      confirmar e salvar
                    </ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={() => setEditMode('email-2')}
                  >
                    <ButtonLabel>voltar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'phone-1' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">alterar celular</h2>
                <span className="md mt-8">
                  Digite o novo número de celular.
                </span>
                <div className="flex flex-column mt-16">
                  <div className="form-container">
                    <label className="form-field-label">
                      número de celular
                    </label>
                    <div className="form-field">
                      <div className="form-field-prefix">+55</div>
                      <input
                        className="caption"
                        type="tel"
                        placeholder="(00) 00000-0000"
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        onInput={(e) => mobilePhoneMask(e as any)}
                      />
                    </div>
                  </div>
                  <Button
                    className="mt-16 mbi-0"
                    full
                    loading={phoneLoading}
                    disabled={
                      newPhone.replace(/\D/g, '').length < 10 || phoneLoading
                    }
                    onClick={phoneSendSms}
                  >
                    <ButtonLabel loading={phoneLoading}>enviar SMS</ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={closeAll}
                  >
                    <ButtonLabel>cancelar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'phone-2' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">confirmar celular</h2>
                <span className="md mt-8">
                  Código enviado para <strong>{newPhone}</strong>
                </span>
                <div className="mt-16">
                  <FormFieldTokenNumber
                    fieldValidate={phoneToken}
                    status={phoneTokenStatus}
                    setState={setPhoneToken}
                    resendingCode={false}
                  />
                </div>
                {phoneLoading && (
                  <div className="user-profile__edit-panel-loader">
                    <Loading />
                  </div>
                )}
                <div className="flex flex-column mt-16">
                  <Button
                    className="mbi-0"
                    customColors="btn-white"
                    full
                    onClick={() => setEditMode('phone-1')}
                  >
                    <ButtonLabel>voltar</ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={closeAll}
                  >
                    <ButtonLabel>cancelar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'address' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">editar endereço</h2>
                <div className="flex flex-column mt-16">
                  <div className="form-container">
                    <label className="form-field-label">CEP</label>
                    <div className="form-field">
                      <input
                        className="caption"
                        placeholder="00000-000"
                        inputMode="numeric"
                        value={addr.cep}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/\D/g, '')
                            .replace(/(\d{5})(\d)/, '$1-$2')
                            .slice(0, 9);
                          setAddr((a) => ({ ...a, cep: v }));
                        }}
                        onBlur={(e) => handleCepBlur(e.target.value)}
                      />
                    </div>
                    <a
                      href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                      target="_blank"
                      rel="noreferrer"
                      className="sm-bold link no-decoration link-intern-text ml-3"
                    >
                      não sei meu CEP
                    </a>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">cidade</label>
                    <div
                      className={`form-field${addrCityDisabled ? ' field-disabled' : ''}`}
                    >
                      <input
                        className="caption"
                        placeholder={
                          addrCityDisabled
                            ? '[preenchimento automático]'
                            : 'Digite sua cidade'
                        }
                        value={addr.city}
                        disabled={addrCityDisabled}
                        onChange={(e) =>
                          setAddr((a) => ({ ...a, city: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">estado</label>
                    <div className="form-field field-disabled">
                      <input
                        className="caption"
                        placeholder="[preenchimento automático]"
                        value={addr.state}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">bairro</label>
                    <div className="form-field">
                      <input
                        className="caption"
                        placeholder="Digite seu bairro"
                        value={addr.neighborhood}
                        onChange={(e) =>
                          setAddr((a) => ({
                            ...a,
                            neighborhood: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">rua / avenida</label>
                    <div className="form-field">
                      <input
                        className="caption"
                        placeholder="Digite a rua/avenida"
                        value={addr.streetName}
                        onChange={(e) =>
                          setAddr((a) => ({
                            ...a,
                            streetName: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="address__number mt-13">
                    <div className="form-container">
                      <label className="form-field-label">número</label>
                      <div
                        className={`form-field${addrNoNumber ? ' field-disabled' : ''}`}
                      >
                        <input
                          className="caption"
                          placeholder={addrNoNumber ? 'SN' : 'Digite o número'}
                          value={addr.addressNumber}
                          disabled={addrNoNumber}
                          onChange={(e) =>
                            setAddr((a) => ({
                              ...a,
                              addressNumber: e.target.value
                            }))
                          }
                        />
                      </div>
                    </div>
                    <label
                      htmlFor="addr-no-number"
                      className="check-box ml-3 mt-6"
                    >
                      <span className="md white-space-nowrap">sem número</span>
                      <input
                        type="checkbox"
                        id="addr-no-number"
                        checked={addrNoNumber}
                        onChange={(e) => setAddrNoNumber(e.target.checked)}
                      />
                      <span className="check-box-checked"></span>
                    </label>
                  </div>
                  <div className="form-container mt-8">
                    <label className="form-field-label">
                      complemento (opcional)
                    </label>
                    <div className="form-field">
                      <input
                        className="caption"
                        placeholder="Digite o complemento"
                        value={addr.complement}
                        onChange={(e) =>
                          setAddr((a) => ({
                            ...a,
                            complement: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                  <Button
                    className="mt-16 mbi-0"
                    full
                    loading={addrLoading}
                    disabled={addrLoading}
                    onClick={saveAddress}
                  >
                    <ButtonLabel loading={addrLoading}>
                      salvar endereço
                    </ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={closeAll}
                  >
                    <ButtonLabel>cancelar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
            {editMode === 'birthDate' && (
              <div className="flex flex-column">
                <h2 className="color-text-default">data de nascimento</h2>
                <span className="md mt-8">
                  Selecione sua data de nascimento.
                </span>
                <div className="flex flex-column mt-16">
                  <div className="form-container">
                    <label className="form-field-label">
                      data de nascimento
                    </label>
                    <div className="form-field">
                      <input
                        className="caption"
                        type="date"
                        value={newBirthDate}
                        onChange={(e) => setNewBirthDate(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    className="mt-16 mbi-0"
                    full
                    loading={saving}
                    disabled={!newBirthDate || saving}
                    onClick={saveBirthDate}
                  >
                    <ButtonLabel loading={saving}>salvar</ButtonLabel>
                  </Button>
                  <Button
                    className="mt-16 mbi-0"
                    customColors="btn-white"
                    full
                    onClick={closeAll}
                  >
                    <ButtonLabel>cancelar</ButtonLabel>
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : currentSubLink.id === 'baixar-dados' ? (
          <div className="up-security-panel">
            <div className="up-security-panel__field-group">
              <h2 className="color-text-default">solicitar dados pessoais</h2>
              <span className="md color-text-default">
                No SBT valorizamos a sua privacidade e queremos ser muito
                transparentes com você.
              </span>
              <span className="md color-text-default">
                Possuímos uma área dedicada para que você faça requisições a
                respeito de seus dados pessoais, conforme previsto na Lei Geral
                de Proteção de Dados Pessoais (Lei 13.709/18 ou LGPD).
              </span>
              <span className="md color-text-default">
                Para entender como tratamos os seus dados pessoais, acesse nossa
                política de privacidade{' '}
                <a
                  href="https://www.sbt.com.br/politica-de-privacidade"
                  target="_blank"
                  rel="noreferrer"
                >
                  clicando aqui
                </a>
                .
              </span>
              <span className="md color-text-default">
                Para solicitar uma cópia dos seus dados pessoais, envie sua
                solicitação para{' '}
                <a href="mailto:dpo@sbt.com.br">dpo@sbt.com.br</a>.
              </span>
            </div>
          </div>
        ) : (
          <div className="user-profile__data-list">
            {rows.map((row, idx) => {
              const isEditingInline = editingField === row.field && !!row.field;
              const isEditingName =
                nameField === row.editType &&
                (row.editType === 'firstName' || row.editType === 'lastName');
              return (
                <div key={idx} className="user-profile__data-row">
                  <div className="user-profile__data-icon">
                    <Icon name={row.icon} />
                  </div>
                  <div className="user-profile__data-body">
                    <p className="user-profile__data-label">{row.label}</p>
                    {isEditingName ? (
                      <div className="user-profile__inline-edit">
                        <input
                          className="user-profile__inline-input"
                          value={nameValue}
                          autoFocus
                          onChange={(e) => setNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNameEdit();
                            if (e.key === 'Escape') setNameField(null);
                          }}
                        />
                        <button
                          className="user-profile__icon-btn"
                          onClick={saveNameEdit}
                          disabled={saving}
                        >
                          <Icon name="checkedSuccessfully" />
                        </button>
                        <button
                          className="user-profile__icon-btn"
                          onClick={() => setNameField(null)}
                        >
                          <Icon name="closeRounded" />
                        </button>
                      </div>
                    ) : isEditingInline ? (
                      <div className="user-profile__inline-edit">
                        <input
                          className="user-profile__inline-input"
                          value={editValue}
                          autoFocus
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveSimpleEdit(row.field!);
                            if (e.key === 'Escape') setEditingField(null);
                          }}
                        />
                        <button
                          className="user-profile__icon-btn"
                          onClick={() => saveSimpleEdit(row.field!)}
                          disabled={saving}
                        >
                          <Icon name="checkedSuccessfully" />
                        </button>
                        <button
                          className="user-profile__icon-btn"
                          onClick={() => setEditingField(null)}
                        >
                          <Icon name="closeRounded" />
                        </button>
                      </div>
                    ) : (
                      <div className="user-profile__data-value-row">
                        <p className="user-profile__data-value">{row.value}</p>
                        {row.editType === 'email' && (
                          <button
                            className="user-profile__icon-btn"
                            onClick={openEmailEdit}
                            title="Editar"
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                        {row.editType === 'phone' && (
                          <button
                            className="user-profile__icon-btn"
                            onClick={openPhoneEdit}
                            title="Editar"
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                        {row.editType === 'address' && (
                          <button
                            className="user-profile__icon-btn"
                            onClick={openAddressEdit}
                            title="Editar"
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                        {row.editType === 'birthDate' && (
                          <button
                            className="user-profile__icon-btn"
                            onClick={openBirthDateEdit}
                            title="Editar"
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                        {(row.editType === 'firstName' ||
                          row.editType === 'lastName') && (
                          <button
                            className="user-profile__icon-btn"
                            title="Editar"
                            onClick={() => {
                              setNameField(
                                row.editType as 'firstName' | 'lastName'
                              );
                              setNameValue(
                                row.value === '\u2014' ? '' : row.value
                              );
                            }}
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                        {row.field && !row.editType && (
                          <button
                            className="user-profile__icon-btn"
                            title="Editar"
                            onClick={() => {
                              setEditingField(row.field!);
                              setEditValue(
                                row.value === '\u2014' ? '' : row.value
                              );
                            }}
                          >
                            <Icon name="editPencil" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
